<template>
  <ValidationObserver ref="form" v-slot="{ failed }" tag="form">
    <app-dialog
      v-model="open"
      primary-action-text="navigation.request"
      secondary-action-text="navigation.cancel"
      title="debtors.limitIncrease.increaseFor"
      :disabled="!amount || !debtor.limit.limitIncreaseRequestAllowed || failed"
      :loading="loading"
      @submit="submitLimitIncreaseRequest"
    >
      <v-row no-gutters>
        <v-col>
          <div class="semi-bold">{{ debtor.name }}</div>
          <span v-if="debtor.address.streetAndHouse">
            {{ debtor.address.streetAndHouse }},&#32;
          </span>
          <span>{{ debtor.address.zip }} {{ debtor.address.city }}</span>
        </v-col>
      </v-row>

      <v-row no-gutters justify="space-between" class="mx-3" style="margin-top: 3.75rem">
        <v-col cols="auto">{{ $t('debtors.limitIncrease.currentLimit') }}:</v-col>
        <v-col class="text-right">
          {{ debtor.limit.approvedLimit | formatCurrency }}
        </v-col>
        <v-col cols="12" class="caption text--muted text-right">
          {{ $t('debtors.since') }}: {{ debtor.limit.limitUpdated | formatDate }}
        </v-col>
      </v-row>

      <v-row v-if="increaseNotAllowedReason" class="mt-6">
        <v-col>
          <app-alert class="mb-4" color="primary" icon="$iconInfo">
            {{ increaseNotAllowedReason }}
          </app-alert>
        </v-col>
      </v-row>

      <template v-else>
        <app-input-row class="mt-6">
          <template #label>
            <div class="d-flex">
              {{ $t('debtors.limitIncrease.wantedLimit') }}:
              <span class="text--small primary--text">*</span>
            </div>
          </template>
          <app-number-field
            v-model="amount"
            placeholder="0,00"
            prepend-inner-icon="$iconEuro"
            name="debtors.limitIncrease.wantedLimit"
            :rules="`required|min_currency:${debtor.limit.approvedLimit}|max_currency:${approvedCreditorLimit}`"
            align-right
            @blur="roundAmount"
          />
        </app-input-row>

        <v-row v-if="hasRoundedAmount" class="mt-2">
          <v-col>
            <app-alert
              color="primary"
              icon="$iconInfo"
              text="debtors.limitIncrease.wantedLimitWasRounded"
            />
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-col cols="12" class="caption primary--text d-flex">
            <span class="text--small">*</span>
            <span>{{ $t('debtors.limitIncrease.conditionsInfo') }}</span>
          </v-col>
        </v-row>
      </template>
    </app-dialog>

    <app-dialog
      v-model="openConfirmation"
      primary-action-text="navigation.ok"
      title="debtors.limitRequestSent"
      @submit="closeConfirmation"
      @close="closeConfirmation"
    >
      <p>{{ $t('debtors.limitIncrease.sent', { amount: formattedAmount }) }}</p>
    </app-dialog>
  </ValidationObserver>
</template>

<script>
import formatCurrency from '@/helper/filter/formatCurrency';
import { NAMESPACE as USER_NAMESPACE } from '@/modules/User/store';
import { NAMESPACE } from '@/modules/Debtors/store';
import { mapActions } from 'vuex';
import ShowDialogMixin from '@/mixins/ShowDialogMixin';

const ROUNDING_STEP_IN_CENT = 500000;

export default {
  name: 'IncreaseLimitDialog',

  mixins: [ShowDialogMixin],

  props: {
    debtor: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    amount: '',
    approvedCreditorLimit: '',
    hasRoundedAmount: false
  }),

  computed: {
    debtorIdentifier() {
      return this.$route.params.id;
    },
    formattedAmount() {
      return formatCurrency(this.amount);
    },
    increaseNotAllowedReason() {
      if (this.debtor.limit.limitIncreaseRequestAllowed) {
        return '';
      }

      const reason = this.debtor.limit.limitIncreaseRequestNotAllowedReason;

      return this.$te(`debtors.limitIncrease.increaseNotAllowedReason.${reason}`)
        ? this.$t(`debtors.limitIncrease.increaseNotAllowedReason.${reason}`)
        : this.$t('debtors.limitIncrease.increaseNotAllowedReason.default');
    }
  },

  methods: {
    ...mapActions(NAMESPACE, ['requestLimitIncrease']),
    ...mapActions(USER_NAMESPACE, ['fetchCreditorLimit']),

    async roundAmount() {
      this.hasRoundedAmount = false;

      if (this.amount === '' || isNaN(this.amount)) {
        return;
      }

      const rounded = Math.ceil(this.amount / ROUNDING_STEP_IN_CENT) * ROUNDING_STEP_IN_CENT;

      if (rounded !== this.amount) {
        this.amount = rounded;
        await this.$nextTick();
        await this.$refs.form.validate();
        this.hasRoundedAmount = true;
      }
    },

    async submitLimitIncreaseRequest() {
      if (!(await this.$refs.form.validate())) {
        return;
      }
      this.loading = true;
      const payload = {
        debtorIdentifier: this.debtorIdentifier,
        data: {
          amount: this.amount
        }
      };

      const { error } = await this.requestLimitIncrease(payload);
      this.loading = false;
      if (error) {
        return;
      }

      this.submitted = true;
    },

    closeConfirmation() {
      this.amount = null;
      this.$refs.form?.reset();
      this.hideAfterSuccess();
    }
  },

  async created() {
    const { creditorLimit, error } = await this.fetchCreditorLimit();

    if (error) {
      this.approvedCreditorLimit = Number.POSITIVE_INFINITY;
      return;
    }

    this.approvedCreditorLimit = creditorLimit.approvedLimit;
  }
};
</script>
